<!-- components/CookieConsent.vue -->
<template>
  <transition name="cookie-slide">
    <div
      v-if="consent.needsDecision"
      class="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 sm:px-4 sm:pb-4"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
    >
      <div class="container mx-auto max-w-3xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        <!-- Main banner view -->
        <div v-if="!consent.showPreferences" class="p-4 sm:p-5">
          <div class="flex items-start gap-3">
            <div class="hidden sm:flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
              <svg class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 9h.01M15 13h.01M10 15h.01M14 9h.01" />
              </svg>
            </div>
            <div class="flex-1">
              <h2 id="cookie-consent-title" class="text-sm font-semibold text-gray-900">
                We value your privacy
              </h2>
              <p class="mt-1 text-xs sm:text-sm text-gray-600 leading-relaxed">
                We use strictly necessary cookies to make the site work. With your permission we'd also
                like to use optional cookies to understand usage and improve ProperFlight. You can accept,
                reject, or choose which to allow. Read more in our
                <router-link to="/cookies" class="text-blue-600 hover:text-blue-500 underline">Cookie Policy</router-link>
                and
                <router-link to="/privacy" class="text-blue-600 hover:text-blue-500 underline">Privacy Policy</router-link>.
              </p>
            </div>
          </div>

          <div class="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
            <button
              type="button"
              @click="consent.showPreferences = true"
              class="order-3 sm:order-1 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Manage preferences
            </button>
            <button
              type="button"
              @click="consent.rejectNonEssential()"
              class="order-2 text-xs sm:text-sm font-medium text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              @click="consent.acceptAll()"
              class="order-1 sm:order-3 text-xs sm:text-sm font-semibold text-white bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Accept all
            </button>
          </div>
        </div>

        <!-- Preferences view -->
        <div v-else class="p-4 sm:p-5">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-gray-900">Cookie preferences</h2>
            <button
              type="button"
              @click="consent.showPreferences = false"
              class="text-gray-400 hover:text-gray-600 p-1 rounded"
              aria-label="Back"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          <div class="mt-3 space-y-3 max-h-[45vh] overflow-y-auto">
            <!-- Strictly necessary -->
            <div class="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3">
              <div>
                <p class="text-sm font-medium text-gray-900">Strictly necessary</p>
                <p class="text-xs text-gray-500 mt-0.5">
                  Required for the site to function, including keeping you signed in and remembering your
                  choices. Always on.
                </p>
              </div>
              <span class="text-xs font-medium text-gray-400 whitespace-nowrap mt-0.5">Always on</span>
            </div>

            <!-- Analytics -->
            <label class="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer">
              <div>
                <p class="text-sm font-medium text-gray-900">Analytics</p>
                <p class="text-xs text-gray-500 mt-0.5">
                  Helps us understand how the site is used so we can improve it.
                </p>
              </div>
              <input
                type="checkbox"
                v-model="localAnalytics"
                class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>

            <!-- Marketing -->
            <label class="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer">
              <div>
                <p class="text-sm font-medium text-gray-900">Marketing</p>
                <p class="text-xs text-gray-500 mt-0.5">
                  Allows third-party partners to measure campaigns and show relevant offers.
                </p>
              </div>
              <input
                type="checkbox"
                v-model="localMarketing"
                class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>
          </div>

          <div class="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              @click="consent.rejectNonEssential()"
              class="text-xs sm:text-sm font-medium text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              @click="savePreferences"
              class="text-xs sm:text-sm font-semibold text-white bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useConsentStore } from '@/stores/consentStore';

const consent = useConsentStore();

const localAnalytics = ref(consent.analytics);
const localMarketing = ref(consent.marketing);

// Keep local toggles in sync when the panel is (re)opened.
watch(
  () => consent.showPreferences,
  (open) => {
    if (open) {
      localAnalytics.value = consent.analytics;
      localMarketing.value = consent.marketing;
    }
  }
);

const savePreferences = () => {
  consent.savePreferences({
    analytics: localAnalytics.value,
    marketing: localMarketing.value,
  });
};
</script>

<style scoped>
.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.cookie-slide-enter-from,
.cookie-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
